import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { createSite, getSites, updateSite, type Site, type SiteSettings as SiteSettingsType } from '../../api/client';
import TrackingCode from './TrackingCode';
import { SITE_KEY } from '../../utils/constants';

export default function SiteSettings() {
  const queryClient = useQueryClient();
  const [activeSiteId, setActiveSiteId] = useState<string>(() => localStorage.getItem(SITE_KEY) || '');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const { data: sites = [] } = useQuery({
    queryKey: ['sites'],
    queryFn: getSites,
  });

  const site = sites.find((s) => s.id === activeSiteId) || sites[0];

  function switchSite(id: string) {
    localStorage.setItem(SITE_KEY, id);
    setActiveSiteId(id);
  }

  const createMutation = useMutation({
    mutationFn: (data: { name: string; domain: string }) => createSite(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      switchSite(created.id);
      setNewName('');
      setNewDomain('');
      setShowAddForm(false);
      setAddError(null);
    },
    onError: () => {
      setAddError('Failed to create site. Check the domain and try again.');
    },
  });

  function handleCreateSite(e: React.FormEvent) {
    e.preventDefault();
    setAddError(null);
    const name = newName.trim();
    const domain = newDomain.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '');
    if (!name || !domain) {
      setAddError('Both a name and a domain are required');
      return;
    }
    createMutation.mutate({ name, domain });
  }

  const [settings, setSettings] = useState<SiteSettingsType>({
    samplingRate: 100,
    recordConsole: true,
    recordNetwork: false,
    maskInputs: true,
    maskText: false,
    retentionDays: 90,
  });

  useEffect(() => {
    if (site?.settings) {
      setSettings(site.settings);
    }
  }, [site]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<SiteSettingsType>) =>
      updateSite(site?.id || '', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites'] }),
  });

  function handleSave() {
    updateMutation.mutate(settings);
  }

  const sitePicker = (
    <div className="card">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">Your Sites</h3>
          <p className="mt-1 text-sm text-gray-500">
            Switch between sites or add a new one to start tracking.
          </p>
          {sites.length > 0 && (
            <select
              value={site?.id || ''}
              onChange={(e) => switchSite(e.target.value)}
              className="input mt-3 w-full"
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.domain}
                </option>
              ))}
            </select>
          )}
        </div>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="btn-primary inline-flex shrink-0 items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Add site
          </button>
        )}
      </div>

      {showAddForm && (
        <form onSubmit={handleCreateSite} className="mt-5 space-y-4 border-t border-gray-200 pt-5">
          {addError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{addError}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="input mt-1"
              placeholder="NDIS Meals"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Domain</label>
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              className="input mt-1"
              placeholder="ndismeals.com.au"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setAddError(null);
                setNewName('');
                setNewDomain('');
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? 'Creating...' : 'Create site'}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  if (!site) {
    return <div className="max-w-2xl space-y-6">{sitePicker}</div>;
  }

  return (
    <div className="max-w-2xl space-y-6">
      {sitePicker}

      {/* Tracking Code */}
      <TrackingCode site={site} />

      {/* Sampling */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900">Session Recording</h3>
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Sampling Rate: {settings.samplingRate}%
            </label>
            <input
              type="range"
              min={1}
              max={100}
              value={settings.samplingRate}
              onChange={(e) =>
                setSettings({ ...settings, samplingRate: Number(e.target.value) })
              }
              className="mt-2 w-full accent-brand-600"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>1%</span>
              <span>100%</span>
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Record Console Logs</p>
                <p className="text-xs text-gray-500">
                  Capture console.log, console.error, etc. during sessions
                </p>
              </div>
              <input
                type="checkbox"
                checked={settings.recordConsole}
                onChange={(e) =>
                  setSettings({ ...settings, recordConsole: e.target.checked })
                }
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
            </label>

            <label className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Record Network Requests</p>
                <p className="text-xs text-gray-500">
                  Capture XHR/Fetch requests and responses
                </p>
              </div>
              <input
                type="checkbox"
                checked={settings.recordNetwork}
                onChange={(e) =>
                  setSettings({ ...settings, recordNetwork: e.target.checked })
                }
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Privacy */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900">Privacy</h3>
        <div className="mt-4 space-y-3">
          <label className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Mask Input Values</p>
              <p className="text-xs text-gray-500">
                Replace input values with asterisks in recordings
              </p>
            </div>
            <input
              type="checkbox"
              checked={settings.maskInputs}
              onChange={(e) =>
                setSettings({ ...settings, maskInputs: e.target.checked })
              }
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Mask Text Content</p>
              <p className="text-xs text-gray-500">
                Replace visible text with placeholders in recordings
              </p>
            </div>
            <input
              type="checkbox"
              checked={settings.maskText}
              onChange={(e) =>
                setSettings({ ...settings, maskText: e.target.checked })
              }
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
          </label>
        </div>
      </div>

      {/* Data Retention */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900">Data Retention</h3>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">
            Keep session data for
          </label>
          <select
            value={settings.retentionDays}
            onChange={(e) =>
              setSettings({ ...settings, retentionDays: Number(e.target.value) })
            }
            className="input mt-1 w-48"
          >
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={updateMutation.isPending}
          className="btn-primary"
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
